import { jsPDF } from 'jspdf';

// PDF del calendario del alumno particular: agenda día por día del período que está viendo
// (horario de sus cursos y clases reservadas con sus docentes).

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const pad = (value) => String(value).padStart(2, '0');
const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const formatHora = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const formatDia = (date) => `${DIAS[date.getDay()]} ${date.getDate()} de ${MESES[date.getMonth()]}`;

const hexToRgb = (hex) => {
  const clean = String(hex || '').replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const value = Number.parseInt(full, 16);
  if (!/^[0-9a-fA-F]{6}$/.test(full) || Number.isNaN(value)) return [71, 85, 105];
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const sanitizeFileName = (value) => String(value || 'calendario')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-zA-Z0-9-_]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase() || 'calendario';

/**
 * @param {object} params
 * @param {Array<{title: string, start: string, end: string, kind: 'curso'|'clase', color?: string, detalle?: string}>} params.items
 * @param {Date} params.desde inicio del rango (inclusive)
 * @param {Date} params.hasta fin del rango (exclusivo, como lo da FullCalendar)
 * @param {string} params.periodo texto del período (título de la vista del calendario)
 * @param {boolean} params.incluirDiasVacios muestra también los días sin actividad (vista semana o día)
 */
export const downloadAlumnoCalendarPdf = ({
  items = [],
  desde,
  hasta,
  periodo = '',
  alumnoNombre = '',
  institucionNombre = '',
  incluirDiasVacios = true
}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const marginTop = 44;
  const marginBottom = 48;
  const contentWidth = pageWidth - marginX * 2;
  let y = marginTop;

  const ensureSpace = (height) => {
    if (y + height <= pageHeight - marginBottom) return;
    doc.addPage();
    y = marginTop;
  };

  // Encabezado
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  doc.text('Mi calendario', marginX, y);
  y += 20;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(71, 85, 105);
  const subtitulo = [periodo, institucionNombre].filter(Boolean).join(' · ');
  if (subtitulo) {
    doc.text(doc.splitTextToSize(subtitulo, contentWidth), marginX, y);
    y += 15;
  }
  if (alumnoNombre) {
    doc.text(`Alumno: ${alumnoNombre}`, marginX, y);
    y += 15;
  }

  // Referencias
  y += 4;
  doc.setFontSize(9);
  doc.setFillColor(71, 85, 105);
  doc.rect(marginX, y - 7, 8, 8, 'F');
  doc.text('Horario de tus cursos', marginX + 13, y);
  doc.setDrawColor(37, 99, 235);
  doc.setLineWidth(1.2);
  doc.rect(marginX + 130, y - 7, 8, 8, 'S');
  doc.text('Clases que reservaste', marginX + 143, y);
  y += 10;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.8);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 18;

  // Agrupar por día
  const porDia = new Map();
  items
    .map((item) => ({ ...item, startDate: new Date(item.start), endDate: new Date(item.end) }))
    .filter((item) => !Number.isNaN(item.startDate.getTime()))
    .sort((a, b) => a.startDate - b.startDate)
    .forEach((item) => {
      const key = toDateKey(item.startDate);
      if (!porDia.has(key)) porDia.set(key, []);
      porDia.get(key).push(item);
    });

  const dias = [];
  if (desde && hasta && incluirDiasVacios) {
    for (let d = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate()); d < hasta; d.setDate(d.getDate() + 1)) {
      dias.push(new Date(d));
    }
  } else {
    [...porDia.keys()].sort().forEach((key) => {
      const [year, month, day] = key.split('-').map(Number);
      dias.push(new Date(year, month - 1, day));
    });
  }

  if (dias.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text('No tenés clases ni cursos en este período.', marginX, y);
  }

  const rowHeight = 30;
  dias.forEach((dia) => {
    const eventos = porDia.get(toDateKey(dia)) || [];
    ensureSpace(26 + Math.max(eventos.length, 1) * rowHeight);

    // Banda del día
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(marginX, y - 13, contentWidth, 20, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(formatDia(dia), marginX + 10, y + 1);
    y += 16;

    if (eventos.length === 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(148, 163, 184);
      doc.text('Sin actividades', marginX + 10, y + 8);
      y += 30;
      return;
    }

    eventos.forEach((evento) => {
      ensureSpace(rowHeight);
      const [r, g, b] = hexToRgb(evento.color);
      const top = y + 2;

      if (evento.kind === 'curso') {
        doc.setFillColor(r, g, b);
        doc.rect(marginX + 4, top, 4, rowHeight - 8, 'F');
      } else {
        doc.setDrawColor(r, g, b);
        doc.setLineWidth(1.5);
        doc.rect(marginX + 4, top, 4, rowHeight - 8, 'S');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(`${formatHora(evento.startDate)} - ${formatHora(evento.endDate)}`, marginX + 16, top + 10);

      const titleX = marginX + 100;
      const titleWidth = contentWidth - 100 - 90;
      doc.text(doc.splitTextToSize(evento.title || 'Actividad', titleWidth)[0], titleX, top + 10);
      if (evento.detalle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(100, 116, 139);
        doc.text(doc.splitTextToSize(evento.detalle, titleWidth)[0], titleX, top + 21);
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(evento.kind === 'curso' ? 'Curso' : 'Clase reservada', pageWidth - marginX - 4, top + 10, { align: 'right' });

      y += rowHeight;
    });

    y += 8;
  });

  // Pie de página
  const totalPages = doc.getNumberOfPages();
  const generado = new Date().toLocaleString('es-AR');
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Generado el ${generado}`, marginX, pageHeight - 24);
    doc.text(`Página ${page} de ${totalPages}`, pageWidth - marginX, pageHeight - 24, { align: 'right' });
  }

  doc.save(`calendario-${sanitizeFileName(periodo || toDateKey(new Date()))}.pdf`);
};

export default downloadAlumnoCalendarPdf;
